<template>
  <div class="sm:c-col-4 md:c-col-8 lg:c-col-6 p-access-key-container">
    <p-form
      @submit="onFormSubmit"
      v-slot="{ invalid }"
    >
      <!-- Título -->
      <div class="c-row">
        <div class="c-col">
          <h3 class="c-text-h3 ui-mb-6">
            Informe o seu email
          </h3>
        </div>
      </div>

      <!-- Subtítulo -->
      <div class="c-row ui-mb-4">
        <div class="c-col">
          <div class="c-text-b1 c-text-gray-400">
            Encaminharemos um email ou SMS com o código de verificação. Caso você não receba em até dois minutos, revise o endereço de email informado.
          </div>
        </div>
      </div>

      <!-- Email -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|email"
          name="Email"
          v-slot="{ errors }"
        >
          <p-input
            v-model="email"
            data-cy="emailField"
            label="Email"
            type="email"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Canal de envio -->
      <div class="c-row ui-mb-4">
        <div class="c-col">
          <c-radio-group v-model="channel" data-cy="channelGroup">
            <c-radio value="email" label="Receber por email" />
            <c-radio value="sms" label="Receber por SMS" />
          </c-radio-group>
        </div>
      </div>

      <!-- Botões -->
      <div class="c-row ui--mb-4">
        <!-- Voltar -->
        <div class="c-col-2 sm:c-col ui-mb-4">
          <template v-if="$slots.back">
            <slot name="back" />
          </template>
          <router-link
            v-else
            :to="{ name: 'login', query: { ...$route.query } }"
          >
            <c-button
              :outline="true"
              class="ui-w-full"
              size="lg"
            >
              Voltar
            </c-button>
          </router-link>
        </div>

        <!-- Enviar -->
        <div class="c-col-2 sm:c-col ui-mb-4">
          <c-button
            data-cy="sendButton"
            size="lg"
            type="submit"
            class="ui-w-full"
            :disabled="invalid"
            :loading="isFormSending"
          >
            Enviar
          </c-button>
        </div>
      </div>
    </p-form>

    <!-- Divisor "Ou" -->
    <div class="login-divider ui-my-6">
      <div class="login-divider__line" />
      <span class="login-divider__text c-text-b2">Ou</span>
      <div class="login-divider__line" />
    </div>

    <!-- Já é aluno? -->
    <div class="c-row ui-mt-8">
      <div class="c-col">
        <div class="ui-flex ui-flex-col c-text-gray">
          <span class="c-text-b1 c-text-gray-500 ui-mb-2"><strong>Já é aluno?</strong></span>
          <template v-if="$slots.login">
            <slot name="login" />
          </template>
          <router-link
            v-else
            :to="{ name: 'login', query: { ...$route.query } }"
            data-cy="cadastroLink"
            class="width-fit"
          >
            <c-link class="padding-remover">
              Faça login com outro email
            </c-link>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CButton, CLink, CRadio, CRadioGroup } from '@estrategiahq/coruja-web-ui';
import PForm from '@/components/p-form';
import PInput from '@/components/p-input';
import { ValidationProvider } from 'vee-validate';
import { rateLimitExceededError } from '@/utils/errors';

export default {
  name: 'PEnviarChaveAcesso',
  props: {
    preventNavigation: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    PForm,
    PInput,
    CButton,
    CLink,
    CRadio,
    CRadioGroup,
    ValidationProvider,
  },
  data: () => ({
    email: '',
    channel: 'email',
    isFormSending: false,
  }),
  mounted() {
    if (this.$route.query.email) {
      this.email = this.$route.query.email.trim();
    }
  },
  methods: {
    async onFormSubmit() {
      this.isFormSending = true;
      try {
        const res = await this.$services.accounts.sendLoginPasswordlessKey(this.email, this.channel);
        const usedChannel = res?.channel || this.channel;
        if (this.preventNavigation) {
          this.$emit('sent', { email: this.email, channel: usedChannel, requestedChannel: this.channel });
        } else {
          this.$router.push({
            name: 'confirme-seu-codigo',
            query: {
              ...this.$route.query,
              email: this.email,
              channel: usedChannel,
              requestedChannel: this.channel,
            },
          });
        }
      } catch (e) {
        this.$toast.show({
          type: 'error',
          text: e === rateLimitExceededError
            ? 'Muitas tentativas. Aguarde alguns minutos e tente novamente.'
            : 'Não foi possível enviar o email. Tente novamente.',
          timeout: 3000,
        });
      } finally {
        this.isFormSending = false;
      }
    },
  },
};
</script>

<style scoped>
.p-access-key-container {
  padding: 2rem !important;
}

.login-divider {
  display: flex;
  align-items: center;
  gap: 16px;
}

.login-divider__line {
  flex: 1;
  height: 1px;
  background: #ccc;
  opacity: 0.48;
}

.login-divider__text {
  color: #9e9e9e;
  white-space: nowrap;
}

.padding-remover {
  padding: 0 !important;
  border: none !important;
}

.width-fit {
  width: fit-content !important;
}
</style>
